import React from 'react'
import {
  fetchAdminBusinessDetail,
  revokeEntitlementOverride,
} from '../../../lib/billingApi'
import { formatMoney } from '../../../lib/utils'
import type {
  AdminBusinessDetail,
  AdminEntitlementOverride,
} from '../../../types/api'

type Tab = 'overview' | 'entitlements' | 'nudge' | 'invoices'

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Overview',
  entitlements: 'Entitlements',
  nudge: 'Nudge activity',
  invoices: 'Invoices',
}

const NUDGE_STATUSES = [
  'PENDING', 'AI_PROCESSING', 'AI_READY', 'SENDING',
  'SENT', 'FAILED', 'REPLY_RECEIVED', 'CONV_ACTIVE',
  'RECOVERED', 'RETURNED', 'NO_SHOW', 'OPT_OUT', 'EXHAUSTED',
]

export function BusinessDetailModal({
  businessId,
  onClose,
}: {
  businessId: string
  onClose: () => void
}) {
  const [detail, setDetail] = React.useState<AdminBusinessDetail | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [tab, setTab] = React.useState<Tab>('overview')

  const load = React.useCallback(() => {
    setLoading(true)
    setError(null)
    fetchAdminBusinessDetail(businessId)
      .then(setDetail)
      .catch(() => setError('Failed to load business detail.'))
      .finally(() => setLoading(false))
  }, [businessId])

  React.useEffect(() => { load() }, [load])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 110,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: 760,
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {detail?.businessName ?? 'Business detail'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                {detail ? `${detail.ownerEmail} · ${detail.planDisplayName}` : businessId}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 18, lineHeight: 1, padding: 4 }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 2, marginTop: 16, marginBottom: -19, borderBottom: 'none' }}>
            {(Object.keys(TAB_LABELS) as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: tab === t ? 600 : 400,
                  color: tab === t ? 'var(--text)' : 'var(--text3)',
                  borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '20px 24px' }}>
          {loading && <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div>}
          {error && (
            <div>
              <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>{error}</div>
              <button className="btn-primary" onClick={load} style={{ padding: '4px 12px', fontSize: 12 }}>Retry</button>
            </div>
          )}
          {!loading && !error && detail && (
            <>
              {tab === 'overview' && <OverviewTab detail={detail} />}
              {tab === 'entitlements' && <EntitlementsTab detail={detail} onChange={load} />}
              {tab === 'nudge' && <NudgeTab detail={detail} />}
              {tab === 'invoices' && <InvoicesTab detail={detail} />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function MetricCard({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', fontFamily: "'DM Mono', monospace", marginTop: 4 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '6px 0', borderBottom: '1px dashed var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text3)' }}>{label}</span>
      <span style={{ color: 'var(--text)', textAlign: 'right' }}>{value ?? '—'}</span>
    </div>
  )
}

function OverviewTab({ detail }: { detail: AdminBusinessDetail }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <MetricCard label="Contacts" value={detail.contactCount} />
        <MetricCard label="Opt-outs" value={detail.optOutCount} />
        <MetricCard label="Active services" value={detail.serviceCount} />
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Business
        </div>
        <KV label="Type" value={detail.businessType} />
        <KV label="Location" value={detail.location} />
        <KV label="Phone" value={detail.phone} />
        <KV label="Joined" value={new Date(detail.createdAt).toLocaleDateString()} />
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Owner
        </div>
        <KV label="Email" value={detail.ownerEmail} />
        <KV label="Name" value={detail.ownerName || '—'} />
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Subscription
        </div>
        <KV label="Plan" value={detail.planDisplayName} />
        <KV label="Status" value={detail.subscriptionStatus ?? 'None'} />
        <KV label="Billing cycle" value={detail.billingCycle ?? '—'} />
        <KV
          label="Period end"
          value={detail.currentPeriodEnd ? new Date(detail.currentPeriodEnd).toLocaleDateString() : '—'}
        />
        <KV
          label="Amount"
          value={detail.amountCents != null && detail.currency
            ? formatMoney(detail.amountCents, detail.currency)
            : '—'}
        />
      </div>
    </div>
  )
}

function EntitlementsTab({ detail, onChange }: { detail: AdminBusinessDetail; onChange: () => void }) {
  const [revoking, setRevoking] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  async function handleRevoke(o: AdminEntitlementOverride) {
    if (!confirm(`Revoke override for ${o.featureKey}?`)) return
    setRevoking(o.id)
    setError(null)
    try {
      await revokeEntitlementOverride(o.id)
      onChange()
    } catch {
      setError('Failed to revoke override.')
    } finally {
      setRevoking(null)
    }
  }

  if (detail.overrides.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text3)', padding: '20px 0' }}>
        No active entitlement overrides. This business uses its plan's default entitlements.
      </div>
    )
  }

  return (
    <div>
      {error && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {detail.overrides.map(o => {
          const value = o.boolValue !== null
            ? (o.boolValue ? 'Enabled' : 'Disabled')
            : (o.intValue === -1 ? 'Unlimited' : String(o.intValue))
          const expired = o.expiresAt && new Date(o.expiresAt) < new Date()
          return (
            <div
              key={o.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 12,
                alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--surface)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', fontFamily: "'DM Mono', monospace" }}>
                  {o.featureKey}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {value}
                  {o.expiresAt && (
                    <span style={{ color: expired ? 'var(--red)' : 'var(--text3)' }}>
                      {' · '}{expired ? 'expired ' : 'expires '}{new Date(o.expiresAt).toLocaleDateString()}
                    </span>
                  )}
                  {o.reason && <span> · {o.reason}</span>}
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
                {new Date(o.createdAt).toLocaleDateString()}
              </div>
              <button
                className="btn-danger"
                disabled={revoking === o.id}
                onClick={() => handleRevoke(o)}
                style={{ padding: '4px 10px', fontSize: 11 }}
              >
                {revoking === o.id ? '…' : 'Revoke'}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function NudgeTab({ detail }: { detail: AdminBusinessDetail }) {
  const counts = detail.nudgeCountsByStatus
  const total = NUDGE_STATUSES.reduce((s, k) => s + (counts[k] ?? 0), 0)
  const max = Math.max(1, ...NUDGE_STATUSES.map(k => counts[k] ?? 0))
  const sent = counts['SENT'] ?? 0
  const recovered = counts['RECOVERED'] ?? 0
  const returned = counts['RETURNED'] ?? 0
  const recoveryRate = sent > 0 ? ((recovered + returned) / sent) * 100 : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <MetricCard label="Total queue rows" value={total} />
        <MetricCard label="Sent" value={sent} />
        <MetricCard label="Recovery rate" value={`${recoveryRate.toFixed(1)}%`} sub={`${recovered + returned} of ${sent}`} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {NUDGE_STATUSES.map(s => {
          const c = counts[s] ?? 0
          return (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 130, fontSize: 12, fontFamily: "'DM Mono', monospace", color: c > 0 ? 'var(--text2)' : 'var(--text3)' }}>
                {s}
              </div>
              <div style={{ flex: 1, height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${(c / max) * 100}%`, height: '100%', background: 'var(--accent)' }} />
              </div>
              <div style={{ width: 36, textAlign: 'right', fontSize: 12, fontFamily: "'DM Mono', monospace", color: c > 0 ? 'var(--text)' : 'var(--text3)' }}>
                {c}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function InvoicesTab({ detail }: { detail: AdminBusinessDetail }) {
  if (detail.recentInvoices.length === 0) {
    return <div style={{ fontSize: 13, color: 'var(--text3)', padding: '20px 0' }}>No invoices yet.</div>
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {['Issued', 'Amount', 'Status', 'Paid', ''].map(h => (
              <th key={h} style={{
                textAlign: 'left',
                padding: '6px 10px',
                fontSize: 11,
                color: 'var(--text3)',
                fontFamily: "'DM Mono', monospace",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detail.recentInvoices.map(inv => (
            <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '8px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text3)' }}>
                {new Date(inv.issuedAt).toLocaleDateString()}
              </td>
              <td style={{ padding: '8px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text)' }}>
                {inv.totalCents != null ? formatMoney(inv.totalCents, inv.currency) : '—'}
              </td>
              <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text2)', fontFamily: "'DM Mono', monospace" }}>
                {inv.status}
              </td>
              <td style={{ padding: '8px 10px', fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text3)' }}>
                {inv.paidAt ? new Date(inv.paidAt).toLocaleDateString() : '—'}
              </td>
              <td style={{ padding: '8px 10px', fontSize: 12 }}>
                {inv.hostedInvoiceUrl && (
                  <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>
                    View
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
